import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listRoles,
  fetchMatrixShape,
  createRole,
  updateRole,
  deleteRole,
  setUserRoleId,
  type RoleAttributes,
} from '@/lib/api/roles';
import { useAuth } from '@/hooks/useAuth';

const KEYS = {
  all: ['roles'] as const,
  list: () => [...KEYS.all, 'list'] as const,
  shape: () => [...KEYS.all, 'shape'] as const,
};

export const useRoles = () =>
  useQuery({ queryKey: KEYS.list(), queryFn: listRoles });

/**
 * The matrix's rows and cells. Effectively static — it only changes when a
 * migration adds a module — so it is cached hard rather than refetched behind
 * every save.
 */
export const usePermissionMatrixShape = () =>
  useQuery({
    queryKey: KEYS.shape(),
    queryFn: fetchMatrixShape,
    staleTime: 60 * 60 * 1000,
  });

/**
 * Saving a role you hold yourself changes what you may do, and the JWT is what
 * says so — until it refreshes, the app would keep offering the permissions you
 * just revoked and the database would keep refusing them. So every mutation
 * here forces a refresh when it touched the caller's own role.
 */
function useRoleMutation<TArgs>(
  fn: (args: TArgs) => Promise<void>,
  touchedRole: (args: TArgs) => string | null,
) {
  const qc = useQueryClient();
  const { claims, refresh } = useAuth();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (_data, args) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: ['users'] });
      if (touchedRole(args) === claims.roleId) await refresh();
    },
  });
}

export const useCreateRole = () =>
  useRoleMutation(
    (a: { id: string; attrs: RoleAttributes; permissions: string[] }) =>
      createRole(a.id, a.attrs, a.permissions),
    () => null,
  );

export const useUpdateRole = () =>
  useRoleMutation(
    (a: { id: string; attrs: RoleAttributes; permissions: string[] }) =>
      updateRole(a.id, a.attrs, a.permissions),
    (a) => a.id,
  );

export const useDeleteRole = () =>
  useRoleMutation((a: { id: string }) => deleteRole(a.id), () => null);

export const useSetUserRoleId = () =>
  useRoleMutation(
    (a: { uid: string; roleId: string }) => setUserRoleId(a.uid, a.roleId),
    () => null,
  );
